import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import {styled} from '@mui/material/styles';

export type VariantStyle = 'accent' | 'secondary'
    | 'filter' | 'regular' | 'tab' | 'text'
export type StyledButtonProps = {
    active?: boolean;
    variantStyle?: VariantStyle;
    isModalButton?: boolean;
    withoutColon?: boolean;
    isDangerous?: boolean;
};

export const StyledButton = styled(Button, {
    shouldForwardProp: (prop) => !['variantStyle'].includes(prop as string) && prop !== 'fullWidth' && prop !== 'isDangerous' && prop !== 'withoutColon' && prop !== 'styleType' && prop !== 'active' && prop !== 'isModalButton',
})<StyledButtonProps>((props) => {
    const {
        variantStyle = 'regular',
        active = false,
        fullWidth = false,
        isModalButton = false,
        withoutColon = false,
        isDangerous = false,
        size,
        theme,
    } = props;
    return {
        width: fullWidth ? '100%' : 'fit-content',


        ...(variantStyle === 'accent' && {
            ...theme.typography.button,
            padding: '16px 24px',
            background: theme.palette.accent,
            borderRadius: 16,
            color: theme.palette.white_100,
            position: 'relative',
            overflow: 'hidden',
            isolation: 'isolate',
            zIndex: 0,

            '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background:
                    'radial-gradient(65.74% 100% at 50% 100%, #5AB600 0%, var(--Accent, #81C73D) 100%)',
                opacity: 0,
                transition: 'opacity 0.25s ease',
                zIndex: -1,
                pointerEvents: 'none',
                borderRadius: 'inherit',
            },

            '&:hover': {
                '&::after': {opacity: 1},
            },
        }),


        ...(variantStyle === 'secondary' && {
            ...theme.typography.body_14_24,
            padding: '12px 16px',
            background: 'white',
            border: 'none',
            boxShadow: `inset 0 0 0 1px ${theme.palette.pale_accent}`,
            borderRadius: 12,
            color: theme.palette.dark,
            textTransform: 'unset',
            transition: 'background-color 0.25s ease, box-shadow 0.25s ease',
            gap: 2,
            '&:hover': {
                background: theme.palette.pale_accent,
                boxShadow: `inset 0 0 0 1px ${theme.palette.pale_accent}`,
            },

            ...(size === 'small' && {
                ...theme.typography.small_description,
                padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                borderRadius: theme.spacing(1),
            }),
            ...(isDangerous && {
                color: theme.palette.white_100,
                boxShadow: `inset 0 0 0 1px ${theme.palette.attention}`,
                background: theme.palette.attention,
                '&:hover': {
                    background: theme.palette.attention_dark,
                    boxShadow: `inset 0 0 0 1px ${theme.palette.attention_dark}`,
                },
            }),
            ...(active && {
                background: theme.palette.pale_accent,
                boxShadow: `inset 0 0 0 1px ${theme.palette.pale_accent}`,
            })

        }),


        ...(variantStyle === 'filter' && {
            position: 'relative',
            overflow: 'hidden',
            isolation: 'isolate',
            zIndex: 0,

            padding: '12px 16px',
            background: 'white',
            border: 'none',
            boxShadow: `inset 0 0 0 1px ${theme.palette.pale_accent}`,
            borderRadius: 8,
            color: theme.palette.semi_dark,
            ...theme.typography.body_14_24,
            textTransform: 'unset',
            transition: 'box-shadow 0.25s ease, color 0.25s ease, background-color 0.25s ease',

            '.additionalText': {
                display: 'flex',
                alignItems: 'center',
                ...theme.typography.subtitle_14_24,
                ...(!withoutColon && {
                    '&::before': {
                        content: '": "',
                        marginRight: 4,
                        ...theme.typography.body_14_24,
                    },
                }),
            },

            ...(active && {
                background: 'transparent',
                color: theme.palette.white_100,
                boxShadow: 'none',

                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: theme.palette.accent,
                    zIndex: -2,
                    borderRadius: 'inherit',
                    pointerEvents: 'none',
                },

                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background:
                        'radial-gradient(65.74% 100% at 50% 100%, #5AB600 0%, var(--Accent, #81C73D) 100%)',
                    opacity: 0,
                    transition: 'opacity 0.25s ease',
                    zIndex: -1,
                    pointerEvents: 'none',
                    borderRadius: 'inherit',
                },

                '&:hover::after': {opacity: 1},
            }),

            ...(!active && {
                '&:hover': {
                    color: theme.palette.semi_dark,
                    boxShadow: `inset 0 0 0 1px ${theme.palette.accent}`,
                },
            }),
        }),


        ...(variantStyle === 'regular' && {
            ...theme.typography.body_14_24,
            padding: `${theme.spacing(3)} ${theme.spacing(4)}`,
            color: theme.palette.dark,
            backgroundColor: theme.palette.pale_accent,
            textTransform: 'unset',
            gap: theme.spacing(2),
            borderRadius: theme.spacing(3),
            ':hover': {
                backgroundColor: theme.palette.accent,
                color: theme.palette.dark,
            },
            ...(size === 'small' && {
                ...theme.typography.small_description,
                padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
                borderRadius: theme.spacing(1),
            }),
        }),


        ...(variantStyle === 'tab' && {
            ...theme.typography.title,
            padding: `${theme.spacing(2)} ${theme.spacing(6)}`,
            color: theme.palette.semi_dark,
            textTransform: 'unset',
            borderRadius: 0,
            '&:hover': {
                background: 'inherit',
                color: theme.palette.dark,
            },

            ...(active && {
                borderBottom: 'none',
                boxShadow: `inset 0 -4px 0 ${theme.palette.accent}`,
            }),
        }),


        '&.Mui-disabled': {
            backgroundColor: theme.palette.backgroundColor,
            boxShadow: `inset 0 0 0 1px ${theme.palette.pale_accent}`,
            opacity: 1,
            color: theme.palette.bgDark,
            svg: {
                color: theme.palette.bgDark,
                path: {
                    stroke: theme.palette.bgDark,
                },
            },
            '&::before': {
                opacity: 0,
            },
            '&::after': {
                opacity: 0,
            },
        },

        ...(variantStyle === 'text' && {
            ...theme.typography.body_14_24,
            color: theme.palette.light_dark,
            padding: 0,
            textDecoration: 'underline',
            textTransform: 'unset',
            '&.Mui-disabled': {
                boxShadow: `unset`,
                color: theme.palette.bgDark,
                opacity: 1,
            },
            ':hover': {
                background: 'transparent',
                textDecoration: 'underline',
                color: isDangerous ? theme.palette.attention : theme.palette.dark,
            },
        }),
    };
});

export type IconButtonVariantStyle = 'accent' | 'secondary'
export type StyledIconButtonProps = {
    active?: boolean;
    variantStyle?: IconButtonVariantStyle;
    borderRadiusVariant?: 'accent' | 'square';
    isBoxShadowDisabled?: boolean;
};

export const StyledIconButton = styled(IconButton, {
    shouldForwardProp: (prop) => prop !== 'active' && prop !== 'variantStyle' && prop !== 'borderRadiusVariant' && prop !== 'isBoxShadowDisabled',
})<StyledIconButtonProps>(({
                               theme,
                               active = false,
                               variantStyle = 'accent',
                               borderRadiusVariant = 'accent',
                               isBoxShadowDisabled = false,
                           }) => {

    return ({
        backgroundColor: theme.palette.white_100,
        borderRadius: 12,
        color: theme.palette.dark,
        boxShadow: isBoxShadowDisabled ? 'unset' : '0 8px 16px 0 rgba(0, 0, 0, 0.15)',
        '&.MuiIconButton-sizeSmall': {
            width: 24,
            height: 24,
        },
        '&.MuiIconButton-sizeMedium': {
            width: 48,
            height: 48,
        },

        '&.MuiIconButton-sizeLarge': {
            width: 56,
            height: 56,
        },
        ':hover': {
            backgroundColor: theme.palette.accent,
            color: theme.palette.white_100,
        },
        '&.Mui-disabled': {
            backgroundColor: theme.palette.backgroundColor,
            color: theme.palette.light_dark,
            boxShadow: `unset`,
        },
        ...(active && {
            backgroundColor: theme.palette.accent,
            color: theme.palette.white_100,
        }),
    });
});